import type { Market } from '@radar/contracts';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useLocation, useMatches, useRouter, useSearch } from '@tanstack/react-router';
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { api, call } from '@/lib/api';
import { qk } from '@/lib/query-keys';

// Marché courant. Priorité : search param `market` de la route > localStorage > premier marché.

export const MARKET_STORAGE_KEY = 'radar.market';

/** Écrans non partitionnés par marché : le sélecteur y est désactivé. */
export const MARKET_AGNOSTIC_PREFIXES = ['/experiments', '/settings', '/calibration'] as const;

export const marketsQueryOptions = () =>
  queryOptions({
    queryKey: qk.markets,
    queryFn: async (): Promise<Market[]> => (await call(api.markets.$get())).items,
    staleTime: Number.POSITIVE_INFINITY,
  });

export function isMarketAgnosticPath(pathname: string): boolean {
  return MARKET_AGNOSTIC_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

export function readMarketParam(search: unknown): string | undefined {
  if (typeof search !== 'object' || search === null || !('market' in search)) return undefined;
  const value = (search as { market?: unknown }).market;
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

type StandardSchemaLike = {
  '~standard': { validate: (value: unknown) => unknown };
};

function isStandardSchema(value: unknown): value is StandardSchemaLike {
  return (
    typeof value === 'object' &&
    value !== null &&
    '~standard' in value &&
    typeof (value as { '~standard'?: { validate?: unknown } })['~standard']?.validate === 'function'
  );
}

/**
 * Une route « déclare `market` » si son schéma zod a une clé `market` (`shape`), ou si son
 * `validateSearch` (fonction ou standard schema) conserve la clé `market` d'une entrée sonde.
 */
export function routeDeclaresMarket(validateSearch: unknown): boolean {
  const probe = { market: '__probe__' };
  try {
    const shape = (validateSearch as { shape?: unknown } | null)?.shape;
    if (typeof shape === 'object' && shape !== null) return 'market' in shape;
    let output: unknown;
    if (typeof validateSearch === 'function') {
      output = validateSearch(probe);
    } else if (isStandardSchema(validateSearch)) {
      const result = validateSearch['~standard'].validate(probe);
      if (result instanceof Promise) return false;
      output = (result as { value?: unknown }).value;
    } else {
      return false;
    }
    return readMarketParam(output) !== undefined;
  } catch {
    return false;
  }
}

export function readStoredMarket(): string | null {
  try {
    return localStorage.getItem(MARKET_STORAGE_KEY);
  } catch {
    return null;
  }
}

const listeners = new Set<() => void>();

export function writeStoredMarket(marketId: string) {
  try {
    localStorage.setItem(MARKET_STORAGE_KEY, marketId);
  } catch {
    // Stockage indisponible : le choix vaut pour la navigation courante.
  }
  for (const listener of listeners) listener();
}

function subscribeStoredMarket(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useStoredMarket(): string | null {
  return useSyncExternalStore(subscribeStoredMarket, readStoredMarket, () => null);
}

/** Résolution pure du marché courant (testée sans React). */
export function resolveMarketId(input: {
  fromSearch: string | undefined;
  stored: string | null;
  markets: Market[] | undefined;
}): string | null {
  const enabled = (input.markets ?? []).filter((market) => market.enabled);
  const known = (id: string | null) =>
    id !== null && enabled.some((market) => market.id === id) ? id : null;
  return input.fromSearch ?? known(input.stored) ?? enabled[0]?.id ?? null;
}

export function useMarket() {
  const { data: markets, isPending } = useQuery(marketsQueryOptions());
  const stored = useStoredMarket();
  const search = useSearch({ strict: false });
  const fromSearch = readMarketParam(search);
  const pathname = useLocation({ select: (location) => location.pathname });
  const matches = useMatches();
  const router = useRouter();

  const declaresMarket = useMemo(
    () =>
      matches.some((match) =>
        routeDeclaresMarket(router.routesById[match.routeId]?.options.validateSearch),
      ),
    [matches, router],
  );

  const enabledMarkets = useMemo(
    () => (markets ?? []).filter((market) => market.enabled),
    [markets],
  );
  const marketId = resolveMarketId({ fromSearch, stored, markets });

  const setMarketId = useCallback(
    (next: string) => {
      writeStoredMarket(next);
      if (declaresMarket) {
        // Search params typés par route : on sérialise nous-mêmes pour rester générique.
        const nextSearch: Record<string, unknown> = { ...search, market: next, page: 1 };
        const href = `${pathname}${router.options.stringifySearch(nextSearch)}`;
        void router.navigate({ href, replace: true });
      }
    },
    [declaresMarket, pathname, router, search],
  );

  return {
    marketId,
    setMarketId,
    markets: enabledMarkets,
    isLoading: isPending,
    declaresMarket,
    isMarketAgnostic: isMarketAgnosticPath(pathname),
  };
}
